/**
 * Admin Reconciliation Tab Component
 * 
 * Displays stuck items requiring reconciliation:
 * - execution_jobs with RECONCILE_REQUIRED or stuck RUNNING
 * - job_outbox stuck in PENDING/FAILED
 * - order_intents stuck in ENTRY_PLACING/ENTRY_ACCEPTED
 */

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import {
  getReconciliationSummary,
  getStuckItems,
  type ReconciliationSummary,
  type StuckItemsResponse,
} from "@app/services/adminReconciliationApi";
import { formatDateTime } from "@app/utils/formatDateTime";
import "./admin-reconciliation.css";

const SUMMARY_QUERY_KEY = ["admin", "reconciliation", "summary"];
const STUCK_ITEMS_QUERY_KEY = ["admin", "reconciliation", "stuck-items"];

export function AdminReconciliationTab(): JSX.Element {
  const [filters, setFilters] = useState<{
    item_type?: string;
    status_filter?: string;
    symbol?: string;
    user_id?: string;
  }>({});
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useQuery<ReconciliationSummary>({
    queryKey: SUMMARY_QUERY_KEY,
    queryFn: getReconciliationSummary,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: stuckItems, isLoading: itemsLoading, isError: itemsError } = useQuery<StuckItemsResponse>({
    queryKey: [STUCK_ITEMS_QUERY_KEY, filters],
    queryFn: () => getStuckItems({ ...filters, limit: 50 }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Update lastUpdated when both queries succeed
  useEffect(() => {
    if (summary && stuckItems && !summaryLoading && !itemsLoading && !summaryError && !itemsError) {
      setLastUpdated(new Date());
    }
  }, [summary, stuckItems, summaryLoading, itemsLoading, summaryError, itemsError]);

  if (summaryLoading || itemsLoading) {
    return (
      <div className="admin-reconciliation-root">
        <Card title="Reconciliation">
          <div className="admin-reconciliation-loading">Loading reconciliation data...</div>
        </Card>
      </div>
    );
  }

  if (summaryError || itemsError) {
    return (
      <div className="admin-reconciliation-root">
        <Card title="Reconciliation">
          <div className="admin-reconciliation-error">Failed to load reconciliation data.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-reconciliation-root">
      <Card
        title="Reconciliation"
        subtitle={
          <span dir="ltr">Last updated: {formatDateTime(lastUpdated)}</span>
        }
      >
        {/* Summary Section */}
        <div className="admin-reconciliation-summary">
          <h3>Summary</h3>
          <div className="admin-reconciliation-cards">
            <div className="admin-reconciliation-card">
              <div className="admin-reconciliation-card__label">Stuck Running Jobs</div>
              <div className={`admin-reconciliation-card__value ${summary?.stuck_running_jobs ? "warning" : ""}`}>
                {summary?.stuck_running_jobs || 0}
              </div>
            </div>
            <div className="admin-reconciliation-card">
              <div className="admin-reconciliation-card__label">Escalated Jobs</div>
              <div className={`admin-reconciliation-card__value ${summary?.escalated_jobs ? "error" : ""}`}>
                {summary?.escalated_jobs || 0}
              </div>
            </div>
            <div className="admin-reconciliation-card">
              <div className="admin-reconciliation-card__label">Stuck Outbox</div>
              <div className={`admin-reconciliation-card__value ${summary?.stuck_outbox ? "warning" : ""}`}>
                {summary?.stuck_outbox || 0}
              </div>
            </div>
            <div className="admin-reconciliation-card">
              <div className="admin-reconciliation-card__label">Stuck Intents</div>
              <div className={`admin-reconciliation-card__value ${summary?.stuck_intents ? "warning" : ""}`}>
                {summary?.stuck_intents || 0}
              </div>
            </div>
            <div className="admin-reconciliation-card">
              <div className="admin-reconciliation-card__label">Total Stuck Items</div>
              <div className={`admin-reconciliation-card__value ${summary?.total_stuck_items ? "error" : ""}`}>
                {summary?.total_stuck_items || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="admin-reconciliation-filters">
          <h3>Filters</h3>
          <div className="admin-reconciliation-filters-grid">
            <div className="admin-reconciliation-filter">
              <label>Item Type</label>
              <select
                value={filters.item_type || ""}
                onChange={(e) =>
                  setFilters({ ...filters, item_type: e.target.value || undefined })
                }
              >
                <option value="">All</option>
                <option value="jobs">Jobs</option>
                <option value="outbox">Outbox</option>
                <option value="intents">Intents</option>
              </select>
            </div>
            <div className="admin-reconciliation-filter">
              <label>Status</label>
              <input
                type="text"
                placeholder="e.g., RUNNING, PENDING"
                value={filters.status_filter || ""}
                onChange={(e) =>
                  setFilters({ ...filters, status_filter: e.target.value || undefined })
                }
              />
            </div>
            <div className="admin-reconciliation-filter">
              <label>Symbol</label>
              <input
                type="text"
                placeholder="e.g., BTC/USDT"
                value={filters.symbol || ""}
                onChange={(e) =>
                  setFilters({ ...filters, symbol: e.target.value || undefined })
                }
              />
            </div>
            <div className="admin-reconciliation-filter">
              <label>User ID</label>
              <input
                type="text"
                placeholder="User ID"
                value={filters.user_id || ""}
                onChange={(e) =>
                  setFilters({ ...filters, user_id: e.target.value || undefined })
                }
              />
            </div>
          </div>
        </div>

        {/* Stuck Jobs Table */}
        {(!filters.item_type || filters.item_type === "jobs") && (
          <div className="admin-reconciliation-section">
            <h3>Stuck Jobs ({stuckItems?.jobs?.count || 0})</h3>
            <div className="admin-reconciliation-table">
              <table>
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Symbol</th>
                    <th>User ID</th>
                    <th>Attempts</th>
                    <th>Last Error</th>
                    <th>Next Reconcile</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {stuckItems?.jobs?.items?.length ? (
                    stuckItems.jobs.items.map((job) => (
                      <tr key={job.job_id}>
                        <td className="admin-reconciliation-cell-id">{job.job_id}</td>
                        <td>{job.job_type}</td>
                        <td className={job.status === "RUNNING" ? "warning" : ""}>{job.status}</td>
                        <td>{job.symbol}</td>
                        <td>{job.user_id}</td>
                        <td className={job.reconcile_attempts > 10 ? "warning" : ""}>
                          {job.reconcile_attempts}
                        </td>
                        <td className="admin-reconciliation-cell-error">
                          {job.last_error || "-"}
                        </td>
                        <td>
                          {job.next_reconcile_at
                            ? new Date(job.next_reconcile_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>{new Date(job.created_at).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9}>No stuck jobs found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stuck Outbox Table */}
        {(!filters.item_type || filters.item_type === "outbox") && (
          <div className="admin-reconciliation-section">
            <h3>Stuck Outbox ({stuckItems?.outbox?.count || 0})</h3>
            <div className="admin-reconciliation-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Job ID</th>
                    <th>Status</th>
                    <th>Attempts</th>
                    <th>Last Error</th>
                    <th>Next Reconcile</th>
                    <th>Created</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {stuckItems?.outbox?.items?.length ? (
                    stuckItems.outbox.items.map((item) => (
                      <tr key={item.id}>
                        <td className="admin-reconciliation-cell-id">{item.id}</td>
                        <td>{item.job_id}</td>
                        <td className={item.status === "PENDING" ? "warning" : "error"}>
                          {item.status}
                        </td>
                        <td className={item.publish_attempts > 5 ? "warning" : ""}>
                          {item.publish_attempts}
                        </td>
                        <td className="admin-reconciliation-cell-error">
                          {item.last_error || "-"}
                        </td>
                        <td>
                          {item.next_reconcile_at
                            ? new Date(item.next_reconcile_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>{new Date(item.created_at).toLocaleString()}</td>
                        <td>{new Date(item.updated_at).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8}>No stuck outbox items found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stuck Intents Table */}
        {(!filters.item_type || filters.item_type === "intents") && (
          <div className="admin-reconciliation-section">
            <h3>Stuck Intents ({stuckItems?.intents?.count || 0})</h3>
            <div className="admin-reconciliation-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Symbol</th>
                    <th>User ID</th>
                    <th>State</th>
                    <th>Order ID</th>
                    <th>Order Link ID</th>
                    <th>Attempts</th>
                    <th>Next Reconcile</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {stuckItems?.intents?.items?.length ? (
                    stuckItems.intents.items.map((intent) => (
                      <tr key={intent.id}>
                        <td className="admin-reconciliation-cell-id">{intent.id}</td>
                        <td>{intent.symbol}</td>
                        <td>{intent.user_id || "-"}</td>
                        <td className="warning">{intent.state}</td>
                        <td>{intent.order_id || "-"}</td>
                        <td>{intent.order_link_id || "-"}</td>
                        <td className={intent.reconcile_attempts > 10 ? "warning" : ""}>
                          {intent.reconcile_attempts}
                        </td>
                        <td>
                          {intent.next_reconcile_at
                            ? new Date(intent.next_reconcile_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>{new Date(intent.updated_at).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9}>No stuck intents found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

